<template>
  <section v-if="!(hideWhenSettled && status?.reason === 'not_pending')" class="ncrm-privacy-doi-resend"
    :aria-label="$t(`${copyKey}.title`)">
    <h4 v-if="!embedded" class="text-subtitle-1 mb-2">{{ $t(`${copyKey}.title`) }}</h4>
    <v-skeleton-loader v-if="loading" type="list-item-two-line" />
    <v-alert v-else-if="loadError" type="error" density="compact">
      {{ $t(`${copyKey}.loadError`) }}
      <template #append>
        <v-btn size="small" variant="text" @click="loadStatus">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <template v-else-if="status">
      <!-- Istoricul vine numai pentru managerul Privacy (`GDPR-095`); ceilalți văd limitele. -->
      <div v-if="!embedded && status.sends" class="text-body-2 mb-1">{{ lastSendText }}</div>
      <div v-if="status.limits" class="text-caption text-medium-emphasis mb-3">
        {{ $t(`${copyKey}.sendsUsed`, { used: status.limits.sends_used, max: status.limits.max_sends }) }}
      </div>
      <v-alert v-if="blockedText" type="info" density="compact" class="mb-3">
        {{ blockedText }}
        <div v-if="status.reason === 'doi_attempt_limit_reached' && smtpLink" class="mt-1">
          <router-link :to="smtpLink">{{ $t(`${copyKey}.configureSmtp`) }}</router-link>
        </div>
      </v-alert>
      <v-alert v-if="resultError" type="error" density="compact" class="mb-3">
        {{ resultError }}
        <div v-if="smtpLink" class="mt-1">
          <router-link :to="smtpLink">{{ $t(`${copyKey}.configureSmtp`) }}</router-link>
        </div>
      </v-alert>
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-email-sync-outline"
        :disabled="!status.eligible"
        :loading="sending"
        @click="resend"
      >
        {{ $t(`${copyKey}.resend`) }}
      </v-btn>
      <div class="text-caption text-medium-emphasis mt-2">{{ $t(`${copyKey}.hint`) }}</div>
    </template>
  </section>
</template>

<script>
import axios from "axios";
import { canAccessCompanySmtpWorkspace } from "@/Utile/ncrmMitpSync.mjs";

// GDPR S6.5 (`GDPR-097`, `GDPR-098`) — retrimiterea manuală a emailului DOI pentru o pereche email + scop
// „în așteptare”, din fișa CRM sau din detaliul dovezii în registrul Privacy. Serverul verifică rolul, tenantul, legătura cu recordul și limitele (3
// trimiteri, 24 h); UI-ul doar explică starea. Limba emailului e cea a capturii. La succes,
// părintele închide drawer-ul și reîncarcă; la eșec, panoul rămâne cu mesajul (`docs/design.md`).
const SMTP_REASONS = Object.freeze(["delivery_unavailable", "sender_config_invalid", "smtp_auth_failed", "smtp_connection_failed"]);
const KNOWN_REASONS = Object.freeze([
  ...SMTP_REASONS, "smtp_rejected_permanent", "smtp_rejected_temporary", "recipient_rejected", "recipient_unroutable",
  "render_failed", "doi_disabled", "doi_cooldown_active", "doi_send_limit_reached", "doi_attempt_limit_reached", "not_pending",
]);

const formatDateTime = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "—";
  const pad = (part) => String(part).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default {
  name: "NcrmPrivacyDoiResendPanel",
  props: {
    // Fișa CRM: recordul + perechea din matrice (`{ contact_point_id, purpose_id, ... }`).
    subjectType: { type: String, default: null },
    subjectId: { type: [Number, String], default: null },
    pair: { type: Object, default: null },
    // Registrul Privacy (`GDPR-098`): evenimentul pending al dovezii, fără record CRM.
    pendingEventId: { type: [Number, String], default: null },
    // În detaliul dovezii, sub `NcrmPrivacyEvidenceDoiStatus`, care are deja titlul și trimiterile.
    embedded: { type: Boolean, default: false },
    // În registru, o dovadă istorică „în așteptare” al cărei acord s-a rezolvat nu arată nimic.
    hideWhenSettled: { type: Boolean, default: false },
  },
  emits: ["sent"],
  data: () => ({ status: null, loading: false, loadError: false, sending: false, resultError: "", resultReason: null, sequence: 0 }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.record.doiResend"; },
    params() {
      if (this.pendingEventId) return { pending_event_id: Number(this.pendingEventId) };
      return {
        subject_type: this.subjectType, subject_id: Number(this.subjectId),
        contact_point_id: Number(this.pair?.contact_point_id), purpose_id: Number(this.pair?.purpose_id),
      };
    },
    lastSendText() {
      const last = this.status?.sends?.at?.(-1);
      if (!last) return this.$t(`${this.copyKey}.neverSent`);
      return this.$t(`${this.copyKey}.lastSend`, {
        status: this.$t(`${this.copyKey}.statuses.${last.status}`), at: formatDateTime(last.created_at),
      });
    },
    blockedText() {
      const reason = this.status?.reason;
      if (!reason) return "";
      if (reason === "not_pending") return this.reasonText(reason);
      return this.$t(`${this.copyKey}.blocked.${reason}`, {
        at: formatDateTime(this.status.limits?.cooldown_until), max: this.status.limits?.max_sends,
        attempts: this.status.limits?.max_attempts,
      });
    },
    // Un refuz SMTP sau plafonul de încercări eșuate (aproape sigur SMTP stricat) duc la configurare.
    smtpLink() {
      const needsSmtp = SMTP_REASONS.includes(this.resultReason) || this.status?.reason === "doi_attempt_limit_reached";
      return needsSmtp && canAccessCompanySmtpWorkspace()
        ? { path: "/ncrm/data-integrations", query: { tab: "smtp" } }
        : null;
    },
  },
  watch: {
    params: { handler() { this.loadStatus(); }, immediate: true, deep: true },
  },
  methods: {
    reasonText(code) {
      return this.$t(`${this.copyKey}.reasons.${KNOWN_REASONS.includes(code) ? code : "other"}`);
    },
    async loadStatus() {
      const sequence = ++this.sequence;
      this.loading = true; this.loadError = false;
      try {
        const { data } = await axios.get("/ncrm/privacy/doi/status", { params: this.params });
        if (sequence === this.sequence) this.status = data;
      } catch {
        if (sequence === this.sequence) this.loadError = true;
      } finally {
        if (sequence === this.sequence) this.loading = false;
      }
    },
    async resend() {
      this.sending = true; this.resultError = ""; this.resultReason = null;
      try {
        const { data } = await axios.post("/ncrm/privacy/doi/resend", this.params);
        if (data.outcome === "accepted" || data.outcome === "uncertain") {
          this.$emit("sent", data.outcome);
          return;
        }
        this.resultReason = data.reason_code;
        this.resultError = this.reasonText(data.reason_code);
        await this.loadStatus();
      } catch (error) {
        this.resultReason = error?.response?.data?.code || null;
        this.resultError = this.$t(`${this.copyKey}.requestError`);
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
